export const REVIEW_LIFECYCLE_PATH = "/teacher-onboarding/deliverables";
export const REVIEW_LIFECYCLE_PARAMS = { summary: "levels", per_page: 1 };

const count = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

/**
 * Do a level row's five counts agree with each other?
 *
 * EXTRACTED, NOT CHANGED (FE-9.3). `lifecycleForLevel` returns `active` for two
 * unrelated reasons, and Review's rail has to tell them apart: a row whose
 * counts contradict themselves is a data failure the vocabulary renames to
 * `not reconciled`, while a row that simply has nothing turned in yet at or
 * below the teacher's lens is an ordinary, correct state. Both arrived as the
 * same word, so the rail could not label either honestly.
 *
 * The predicate is exported rather than copied into Review because it IS the
 * invariant: a second transcription of it would drift from this one and the
 * drift would be invisible until a level was mislabelled.
 *
 * @returns {boolean} false when a count is absent or the invariant is violated
 */
export const levelCountsReconcile = (row) => {
  const submitted = count(row?.submitted_total);
  const graded = count(row?.graded_total);
  const pending = count(row?.pending_total);
  const missing = count(row?.missing_total);
  const squads = count(row?.squads_total);

  if (
    [submitted, graded, pending, missing, squads].some((item) => item === null)
  ) {
    return false;
  }
  return !(
    graded > submitted ||
    pending > submitted ||
    graded + pending !== submitted ||
    submitted + missing !== squads
  );
};

export const lifecycleForLevel = (row, expectedLevel) => {
  const submitted = count(row?.submitted_total);
  const graded = count(row?.graded_total);
  const missing = count(row?.missing_total);
  const pending = count(row?.pending_total);

  if (!levelCountsReconcile(row)) return "active";
  if (pending > 0) return "review_needed";
  if (submitted > 0 && missing > 0) return "submissions_arriving";
  if (submitted > 0 && missing === 0 && graded === submitted) return "reviewed";
  if (submitted === 0 && Number(row?.level_number) > Number(expectedLevel)) {
    return "upcoming";
  }
  return "active";
};

export const readLifecycleSnapshot = (body, expectedLevel) => {
  const payload = Array.isArray(body?.data) ? body : (body?.data ?? body);
  const aggregate = payload?.aggregate;
  const generatedAt = payload?.generated_at;
  const timestamp = Date.parse(generatedAt);
  if (
    aggregate?.mode !== "levels" ||
    !Array.isArray(aggregate?.levels) ||
    !Number.isFinite(timestamp)
  ) {
    return null;
  }

  const levels = aggregate.levels.map((row) => ({
    ...row,
    lifecycle: lifecycleForLevel(row, expectedLevel),
  }));
  const pendingCounts = levels.map((row) => count(row.pending_total));

  return {
    generatedAt,
    timestamp,
    levels,
    pending: pendingCounts.some((pending) => pending === null)
      ? null
      : pendingCounts.reduce((sum, pending) => sum + pending, 0),
  };
};

export const shouldAcceptLifecycleSnapshot = (current, incoming) =>
  !!incoming && (!current || incoming.timestamp >= current.timestamp);
